import { Pencil } from "lucide-react";
import { Button } from "../../elements/button/button";
import { Text } from "../../elements/text/text";
import { IconGeoLocation } from "../../icons/geo-location-icon";
import { IconReceipt } from "../../icons/receipt-icon";
import { IconTruck } from "../../icons/truck-icon";
import type { UserRole } from "../user-card-profile/user-card-profile";

export type AddressType = "shipping" | "billing";

export interface PreferredAddress {
  label: string;
  companyName: string;
  street: string;
  postalCodeCity: string;
  country: string;
}

export interface AddressListItem {
  id: string;
  fullAddress: string;
  type: AddressType;
}

export interface UserCardAddressProps {
  microcopies: {
    sectionTitle: string;
    preferredShippingLabel: string;
    preferredBillingLabel: string;
    footerNote: string;
    shippingLegend: string;
    billingLegend: string;
  };
  preferredShipping: PreferredAddress;
  preferredBilling: PreferredAddress;
  addresses: AddressListItem[];
  onEditShipping?: () => void;
  userRole?: UserRole;
}

function AddressTypeIcon(props: Readonly<{ type: AddressType }>) {
  if (props.type === "shipping") {
    return <IconTruck color="muted" size="lg" />;
  }
  return <IconReceipt color="muted" size="lg" />;
}

export function UserCardAddress(props: Readonly<UserCardAddressProps>) {
  const shippingAddressCount = props.addresses.filter(
    (address) => address.type === "shipping"
  ).length;

  return (
    <section
      aria-label={props.microcopies.sectionTitle}
      className="rounded-md bg-background p-4"
    >
      {/* Section Header */}
      <div className="flex items-center gap-2 py-4 md:px-3">
        <IconGeoLocation color="muted" size="lg" />
        <Text variant="h4">{props.microcopies.sectionTitle}</Text>
      </div>

      {/* Preferred Address Cards */}
      <div className="grid grid-cols-1 gap-6 pb-3 md:grid-cols-2 md:px-3">
        {/* Preferred Shipping */}
        <div className="rounded-md border border-border p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <IconTruck className="text-zinc-500" size="lg" />
              <Text variant="body-sm-bold">
                {props.microcopies.preferredShippingLabel}
              </Text>
            </div>
            {props.userRole !== "SHOP_VIEWER" &&
              props.userRole !== "UNKNOWN" &&
              shippingAddressCount > 1 && (
                <Button
                  aria-label={props.microcopies.preferredShippingLabel}
                  onClick={props.onEditShipping}
                  size="icon-sm"
                  variant="ghost"
                >
                  <Pencil className="size-6 text-zinc-500" />
                </Button>
              )}
          </div>
          <div className="flex flex-col gap-0.5">
            <Text variant="body-md">{props.preferredShipping.companyName}</Text>
            <Text variant="body-md">{props.preferredShipping.street}</Text>
            <Text variant="body-md">
              {props.preferredShipping.postalCodeCity}
            </Text>
            <Text variant="body-md">{props.preferredShipping.country}</Text>
          </div>
        </div>

        {/* Preferred Billing */}
        <div className="rounded-md border border-border p-4">
          <div className="mb-3 flex items-center gap-2">
            <IconReceipt className="text-zinc-500" size="lg" />
            <Text variant="body-sm-bold">
              {props.microcopies.preferredBillingLabel}
            </Text>
          </div>
          <div className="flex flex-col gap-0.5">
            <Text variant="body-md">{props.preferredBilling.companyName}</Text>
            <Text variant="body-md">{props.preferredBilling.street}</Text>
            <Text variant="body-md">
              {props.preferredBilling.postalCodeCity}
            </Text>
            <Text variant="body-md">{props.preferredBilling.country}</Text>
          </div>
        </div>
      </div>

      {/* Address List */}
      {props.addresses.length > 0 && (
        <div className="md:px-3">
          <ul>
            {props.addresses.map((address) => (
              <li
                className="flex items-center justify-between border-border border-b px-4 py-4"
                key={address.id}
              >
                <Text variant="body-md">{address.fullAddress}</Text>
                <div className="shrink-0 pl-4 text-zinc-500">
                  <AddressTypeIcon type={address.type} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer */}
      <div className="flex flex-col gap-4 py-5 pt-3 md:flex-row md:items-center md:justify-between md:px-3">
        <Text variant="body-md">{props.microcopies.footerNote}</Text>
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
          <div className="flex items-center gap-1.5">
            <IconTruck color="muted" size="lg" />
            <Text color="muted" variant="body-xs">
              {props.microcopies.shippingLegend}
            </Text>
          </div>
          <div className="flex items-center gap-1.5">
            <IconReceipt color="muted" size="lg" />
            <Text color="muted" variant="body-xs">
              {props.microcopies.billingLegend}
            </Text>
          </div>
        </div>
      </div>
    </section>
  );
}
